<script setup lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const { field, block, addItem, removeItem, updateItem } = useInlineBlockEditor(props.id);

const defaultTab = field<string>("defaultTab", "");
const items = computed<any[]>(() => block.value?.children ?? []);

const addTab = () => {
  const newId = crypto.randomUUID();
  addItem("children", {
    id: newId,
    title: t("defaultItemTitle"),
    componentId: ComponentDefinitionIds.TAB_ITEM,
    children: [],
  });
  if (!defaultTab.value) defaultTab.value = newId;
};

const removeTab = (index: number) => {
  const removed = items.value[index];
  const nextItems = items.value.filter((_, itemIndex) => itemIndex !== index);
  removeItem("children", index);
  if (removed?.id === defaultTab.value) {
    defaultTab.value = nextItems[0]?.id ?? "";
  }
};
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:app-window" accent="container">
    <EditorField :label="t('defaultTabLabel')">
      <Select :model-value="defaultTab" @update:model-value="(value) => (defaultTab = value as string)">
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue :placeholder="t('defaultTabPlaceholder')" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="item in items" :key="item.id" :value="item.id">
            {{ item.title || t("untitledItem") }}
          </SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('itemsLabel')" :hint="t('itemsHint')">
      <div class="space-y-2">
        <div v-for="(item, index) in items" :key="item.id ?? index" class="flex items-center gap-2">
          <Input
            :model-value="item.title"
            class="h-8 flex-1 rounded-sm bg-muted text-[13px]"
            :placeholder="t('itemTitlePlaceholder')"
            @update:model-value="(value) => updateItem('children', index, { title: value })"
          />
          <Button variant="ghost" size="icon" class="size-8 shrink-0 text-destructive" @click="removeTab(index)">
            <Icon mode="svg" size="16" name="lucide:x" />
          </Button>
        </div>
      </div>
    </EditorField>

    <Button variant="outline" size="sm" class="self-start rounded-sm text-[13px]" @click="addTab">
      <Icon mode="svg" size="16" name="lucide:plus" class="mr-1.5" />
      {{ t("addTab") }}
    </Button>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "Tabs"
  defaultTabLabel: "Default Tab"
  defaultTabPlaceholder: "Select default tab"
  itemsLabel: "Tab Items"
  itemsHint: "Use the main editor to add blocks inside each tab item."
  itemTitlePlaceholder: "Tab item title"
  untitledItem: "Untitled tab"
  defaultItemTitle: "New Tab"
  addTab: "Add Tab Item"
</i18n>
